import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { EyeOff, Fingerprint, Shield } from 'lucide-react';
import { ScrambleText } from './ScrambleText';

const metadata = [
  { label: 'ALIAS', value: 'ZATO' },
  { label: 'ACTIVE_SINCE', value: '2019' },
  { label: 'MEDIUM', value: 'PHYSICAL / DIGITAL' },
  { label: 'IDENTITY', value: 'REDACTED' },
];

function AboutVideo({ isInView }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -40 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      <div className="about-image-container" data-testid="about-image">
        <video
          src="/vault-genesis.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="about-image"
        />
      </div>
    </motion.div>
  );
}

function AboutKnownUnknown() {
  return (
    <div className="grid grid-cols-2 gap-6 mb-2" data-testid="about-known-unknown">
      <div>
        <p className="font-mono text-xs text-[#FFC700] tracking-widest mb-2">KNOWN</p>
        <ul className="font-mono text-sm text-white/60 space-y-1">
          <li>Artist.</li>
          <li>Collector.</li>
          <li>Designer.</li>
          <li>Observer.</li>
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs text-white/30 tracking-widest mb-2">UNKNOWN</p>
        <ul className="font-mono text-sm text-white/40 space-y-1">
          <li>Name.</li>
          <li>Location.</li>
          <li>Face.</li>
          <li>Identity.</li>
        </ul>
      </div>
    </div>
  );
}

function AboutBioText() {
  return (
    <>
      <p className="body-text text-lg leading-relaxed" data-testid="about-bio">
        ZATO chose anonymity. Identity is deliberately secondary here — not because there is nothing to say, but because the ideas matter more than the person holding them.
      </p>
      <p className="body-text leading-relaxed text-white/60">
        Privacy is not secrecy. Financial privacy is personal privacy. Sovereignty requires control over one's own identity, information and value — and ZATO is interested in the systems that determine how those things get recorded.
      </p>
      <p className="body-text leading-relaxed text-white/60">
        Art is one way those ideas become physical. Cryptography is another. Objects can carry ideas further than words can.
      </p>
    </>
  );
}

function AboutMetadataPanel({ isInView }) {
  return (
    <div className="terminal-panel mt-8" data-testid="about-metadata">
      <div className="terminal-panel-header">
        <span className="terminal-panel-dot" />
        ARTIST_PROFILE :: METADATA_v1.0
        <span className="ml-auto opacity-40">READ-ONLY</span>
      </div>
      <div className="metadata-grid p-4">
        {metadata.map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 + i * 0.08 }}
            className="metadata-row"
          >
            <span className="metadata-label">{item.label}</span>
            <ScrambleText
              text={item.value}
              trigger="hover"
              className="metadata-value"
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function AboutTraits() {
  return (
    <div className="flex gap-3 pt-4 flex-wrap">
      <div className="trait-badge"><Fingerprint size={12} /> <span>PSEUDONYMOUS</span></div>
      <div className="trait-badge"><Shield size={12} /> <span>PRIVACY-FIRST</span></div>
      <div className="trait-badge"><EyeOff size={12} /> <span>ANONYMOUS</span></div>
    </div>
  );
}

export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="about" className="py-32 px-6 relative overflow-hidden" data-testid="about-section" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFC700]/30 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="mono-tag">// SECTION_01 :: WHO_IS_ZATO</span>
          <h2 className="section-title mt-4">WHO IS<br /><span className="text-[#FFC700]">ZATO</span></h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-16 items-start">
          <AboutVideo isInView={isInView} />

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="space-y-8"
          >
            <AboutKnownUnknown />
            <AboutBioText />
            <AboutMetadataPanel isInView={isInView} />
            <AboutTraits />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
